import React, { useState } from "react";
import {
  Button,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";
import Flatpickr from "react-flatpickr";
import moment from "moment";
import { AsyncPaginatedSelect } from "components/common/AsyncPaginatedSelect";
import { getModeOfPaymentOptions } from "features/Meta/services/MetaService";
import { payrollItemEmployeeLabel } from "features/Payroll/utils/payrollEmployeeDisplay";
import type { PayrollItem } from "features/Payroll/types/payroll.types";

function itemNetAmount(item: PayrollItem | null | undefined): number {
  return Number(item?.netPay ?? item?.netAmount ?? 0);
}

interface PayPayrollItemModalProps {
  show?: boolean;
  item?: PayrollItem | null;
  submitting?: boolean;
  onCloseClick?: () => void;
  onConfirm: (payload: {
    modeOfPaymentId?: number;
    paymentDate?: string | null;
    referenceNumber?: string | null;
  }) => void;
}

const PayPayrollItemModal = ({
  show,
  item,
  submitting = false,
  onCloseClick,
  onConfirm,
}: PayPayrollItemModalProps) => {
  const [modeOfPaymentId, setModeOfPaymentId] = useState<number | "">("");
  const [paymentDate, setPaymentDate] = useState("");
  const [referenceNumber, setReferenceNumber] = useState("");
  const [attempted, setAttempted] = useState(false);

  const hasPositiveNet = itemNetAmount(item) > 0;

  const handleClose = () => {
    setModeOfPaymentId("");
    setPaymentDate("");
    setReferenceNumber("");
    setAttempted(false);
    onCloseClick?.();
  };

  const handleConfirm = () => {
    setAttempted(true);
    if (hasPositiveNet && !modeOfPaymentId) return;
    onConfirm({
      modeOfPaymentId: modeOfPaymentId ? Number(modeOfPaymentId) : undefined,
      paymentDate: paymentDate || null,
      referenceNumber: referenceNumber.trim() || null,
    });
  };

  return (
    <Modal fade isOpen={show} toggle={handleClose} centered>
      <ModalHeader toggle={handleClose}>
        Pay individually — {item ? payrollItemEmployeeLabel(item) : ""}
      </ModalHeader>
      <ModalBody>
        <p className="text-muted mb-3">
          Use this only as a fallback (e.g. re-paying one employee after a
          bounced transfer) — the run-level <strong>Pay run</strong> action is
          the normal way to pay salaries.
        </p>
        {hasPositiveNet ? (
          <>
            <div className="mb-3">
              <Label className="form-label">
                Mode of payment <span className="text-danger">*</span>
              </Label>
              <AsyncPaginatedSelect
                fetchOptions={getModeOfPaymentOptions}
                placeholder="Search modes…"
                value={modeOfPaymentId}
                onChange={(val: any) => setModeOfPaymentId(val?.value ?? "")}
                isInvalid={attempted && !modeOfPaymentId}
              />
              {attempted && !modeOfPaymentId ? (
                <FormFeedback type="invalid" className="d-block">
                  Mode of payment is required.
                </FormFeedback>
              ) : null}
            </div>
            <div className="mb-3">
              <Label className="form-label">
                Payment date <span className="text-muted fw-normal">(optional)</span>
              </Label>
              <Flatpickr
                className="form-control"
                placeholder="Select date"
                options={{ altInput: true, altFormat: "d M, Y", dateFormat: "d M, Y" }}
                onChange={(date: any) =>
                  setPaymentDate(date[0] ? moment(date[0]).format("YYYY-MM-DD") : "")
                }
                value={paymentDate ? new Date(paymentDate) : ""}
                disabled={submitting}
              />
            </div>
            <div className="mb-0">
              <Label className="form-label">
                Reference number{" "}
                <span className="text-muted fw-normal">(optional)</span>
              </Label>
              <Input
                type="text"
                placeholder="UTR, cheque no., transaction id…"
                value={referenceNumber}
                onChange={(e) => setReferenceNumber(e.target.value)}
                disabled={submitting}
              />
            </div>
          </>
        ) : (
          <p className="text-muted mb-0">
            This item nets to ₹0 — no payment method is needed. It will move
            straight to <strong>Paid</strong> (settled, no disbursement).
          </p>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="light" disabled={submitting} onClick={handleClose}>
          Cancel
        </Button>
        <Button color="success" disabled={submitting} onClick={handleConfirm}>
          {submitting ? "Please wait…" : "Pay"}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default PayPayrollItemModal;
